import { useState, useCallback, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { apiAdminResyncOrganisationLanguages } from '../../common/api/admin';
import { callMethod } from '../../common/utilsClient/ddp/actions';

/**
 * TECH-2730. The Refresh call for the languages panel, with its state KEYED
 * TO THE ORGANISATION IT WAS ISSUED FOR — one entry per organisation, not one
 * slot for the whole screen.
 *
 * The first version kept two screen-level booleans, `refreshing` and
 * `failed`, and reset `failed` in an effect on organisation change. That did
 * not close the window it claimed to: click Refresh on A (a 60s aggregate),
 * switch the dropdown to B — the effect clears the flag, then A's rejection
 * arrives and sets it again, so B's panel says "could not be recomputed" for
 * a refresh B never had; and `refreshing` stayed true, so B showed
 * "Recomputing…" with Refresh disabled for the rest of A's call.
 *
 * The second version remembered ONE id per state, which held for one
 * organisation at a time and broke for two: Refresh A, switch to B, Refresh B
 * (B's button is live, A is not B), B fails and shows it — then A fails and
 * overwrites the slot, and B's failure line vanishes while B is selected.
 * And with A and B both in flight, switching back to A showed A's button
 * live, because the slot held B; a second click was refused by the server
 * ("already being recomputed") and A showed a failure it did not have.
 *
 * So: SETS of organisation ids. A pending request adds its own id and
 * removes it when it settles, whatever else settled in between; a failure
 * adds its id and the next refresh of THAT organisation removes it. What the
 * panel shows for the selected organisation is that organisation's own
 * state, however many others are in flight.
 */
const withId = (set, id) => {
  if (set.has(id)) {
    return set;
  }
  const next = new Set(set);
  next.add(id);
  return next;
};

const withoutId = (set, id) => {
  if (!set.has(id)) {
    return set;
  }
  const next = new Set(set);
  next.delete(id);
  return next;
};

/**
 * The server's own code for "a recompute of this organisation is already
 * running" — from the Resync-all loop holding it, or another administrator's
 * Refresh. Not a failure: the numbers ARE being recomputed. Saying "could not
 * be recomputed, the numbers are unchanged" here was false (review round 4).
 */
const isAlreadyRunning = (error) =>
  !!error &&
  typeof error.error === 'string' &&
  error.error.endsWith('.alreadyRunning');

const snapshotMs = (value) => (value instanceof Date ? value.getTime() : 0);

/**
 * How long the "already recomputing" line stays up at most. The server caps
 * one aggregate at COUNT_MAX_TIME_MS (60s); after that the run this refusal
 * was about has either written a newer snapshot (which clears the line
 * itself) or failed and written nothing — and then there is nothing left to
 * wait for, so the line goes. Exported for the test.
 */
export const ALREADY_RUNNING_HIDE_MS = 90 * 1000;

/**
 * @param {string|null} organisationId the organisation currently selected
 * @param {Date|null} patientLanguageCountsAt that organisation's snapshot
 *   time, from the document the page subscribes to.
 *
 * THE "ALREADY RECOMPUTING" LINE, AND WHAT CLEARS IT (review rounds 4–6).
 * It is tied to the snapshot that was current at the click, so a NEWER
 * snapshot — the holding run succeeded — clears it on its own. But the
 * holding run can also FAIL (a maxTimeMS expiry, expected on dev3's first
 * pass), and then nothing changes on the document: an earlier version left
 * the line up indefinitely, promising an update that would never come. So the
 * copy promises nothing ("was already recomputing when you pressed — press
 * Refresh again in a minute") and the line also times out after
 * ALREADY_RUNNING_HIDE_MS, past which no run it could refer to is still going.
 * The next refresh of that organisation clears it too.
 */
export default function useRefreshCounts(
  organisationId,
  patientLanguageCountsAt,
) {
  const dispatch = useDispatch();
  // The ids whose refresh is in flight, the ids whose last refresh failed,
  // and — keyed by id — the snapshot time that was current when the server
  // said someone else was already recomputing that organisation.
  const [refreshingFor, setRefreshingFor] = useState(() => new Set());
  const [failedFor, setFailedFor] = useState(() => new Set());
  const [alreadyRunningFor, setAlreadyRunningFor] = useState(() => new Map());
  // One hide timer per organisation, cleared on unmount.
  const hideTimers = useRef(new Map());

  useEffect(
    () => () => {
      hideTimers.current.forEach((timer) => clearTimeout(timer));
      hideTimers.current.clear();
    },
    [],
  );

  const forgetAlreadyRunning = useCallback((usersGroupId) => {
    const timer = hideTimers.current.get(usersGroupId);
    if (timer) {
      clearTimeout(timer);
      hideTimers.current.delete(usersGroupId);
    }
    setAlreadyRunningFor((current) => {
      if (!current.has(usersGroupId)) {
        return current;
      }
      const next = new Map(current);
      next.delete(usersGroupId);
      return next;
    });
  }, []);

  const refresh = useCallback(() => {
    const usersGroupId = organisationId;
    if (!usersGroupId || refreshingFor.has(usersGroupId)) {
      return;
    }
    // Captured at the click: the refusal, if it comes, is about THIS snapshot.
    const snapshotAtClick = snapshotMs(patientLanguageCountsAt);
    setRefreshingFor((current) => withId(current, usersGroupId));
    setFailedFor((current) => withoutId(current, usersGroupId));
    forgetAlreadyRunning(usersGroupId);
    dispatch(callMethod(apiAdminResyncOrganisationLanguages, { usersGroupId }))
      // Nothing to store on success: the method writes the organisation
      // document and the subscription delivers the new numbers.
      .then(() => {
        setRefreshingFor((current) => withoutId(current, usersGroupId));
        return null;
      })
      .catch((error) => {
        setRefreshingFor((current) => withoutId(current, usersGroupId));
        if (isAlreadyRunning(error)) {
          setAlreadyRunningFor((current) =>
            new Map(current).set(usersGroupId, snapshotAtClick),
          );
          hideTimers.current.set(
            usersGroupId,
            setTimeout(
              () => forgetAlreadyRunning(usersGroupId),
              ALREADY_RUNNING_HIDE_MS,
            ),
          );
        } else {
          setFailedFor((current) => withId(current, usersGroupId));
        }
      });
  }, [
    dispatch,
    organisationId,
    patientLanguageCountsAt,
    refreshingFor,
    forgetAlreadyRunning,
  ]);

  return {
    refreshingCounts: !!organisationId && refreshingFor.has(organisationId),
    refreshCountsFailed: !!organisationId && failedFor.has(organisationId),
    // Only while the snapshot is still the one the refusal was about, and
    // only until the hide timer runs out.
    refreshAlreadyRunning:
      !!organisationId &&
      alreadyRunningFor.has(organisationId) &&
      alreadyRunningFor.get(organisationId) ===
        snapshotMs(patientLanguageCountsAt),
    refresh,
  };
}
